import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  TablePagination,
  Skeleton,
  Button,
  Divider,
  MenuItem,
} from '@mui/material';
import {
  Search,
  Refresh,
  Person,
  Email,
  Phone,
  Business,
  Visibility,
  Group,
  Star,
  Edit,
  Save,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import crmService, { Contact, Account } from '../services/crmService';

const ContactsPage: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Detail dialog state
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [viewingContact, setViewingContact] = useState<Contact | null>(null);

  // Edit dialog state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobile: '',
    title: '',
    department: '',
    type: 'Other' as Contact['type'],
    description: '',
  });
  const [saving, setSaving] = useState(false);

  // Fetch contacts
  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await crmService.getContacts();
      const contactsData = Array.isArray(response) ? response :
                          response.contacts ? response.contacts :
                          response.data ? response.data : [];
      setContacts(contactsData);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setSnackbar({ open: true, message: 'Failed to fetch contacts', severity: 'error' });
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  // Filter contacts based on search
  const filteredContacts = contacts.filter(contact =>
    `${contact.firstName} ${contact.lastName} ${contact.email} ${contact.title}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get type color
  const getTypeColor = (type?: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'default'> = {
      'Decision Maker': 'success',
      'Influencer': 'primary',
      'End User': 'info',
      'Technical': 'secondary',
      'Executive': 'warning',
      'Other': 'default'
    };
    return colors[type || 'Other'] || 'default';
  };

  // Get account name from contact
  const getAccountName = (account: string | Account | undefined): string => {
    if (!account) return '-';
    if (typeof account === 'string') return account;
    return account.name || '-';
  };

  // Handle view contact details
  const handleViewContact = (contact: Contact) => {
    setViewingContact(contact);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setViewingContact(null);
  };

  // Handle edit contact
  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setEditFormData({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      title: contact.title || '',
      department: contact.department || '',
      type: contact.type || 'Other',
      description: contact.description || '',
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingContact(null);
  };

  // Handle form change
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle save contact
  const handleSaveContact = async () => {
    if (!editingContact?._id) return;

    try {
      setSaving(true);
      await crmService.updateContact(editingContact._id, editFormData);
      setSnackbar({ open: true, message: 'Contact updated successfully', severity: 'success' });
      handleCloseEditDialog();
      fetchContacts();
    } catch (error) {
      console.error('Error updating contact:', error);
      setSnackbar({ open: true, message: 'Failed to update contact', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Stats
  const totalContacts = contacts.length;
  const primaryContacts = contacts.filter(c => c.isPrimary).length;
  const decisionMakers = contacts.filter(c => c.type === 'Decision Maker').length;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Group sx={{ color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{totalContacts}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Contacts</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Star sx={{ color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{primaryContacts}</Typography>
                  <Typography variant="body2" color="text.secondary">Primary Contacts</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Person sx={{ color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{decisionMakers}</Typography>
                  <Typography variant="body2" color="text.secondary">Decision Makers</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight="bold">
            Contacts
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchContacts}
          >
            Refresh
          </Button>
        </Box>

        {/* Search Bar */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search contacts by name, email, or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Contacts Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={7}>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredContacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No contacts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredContacts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((contact) => (
                      <TableRow key={contact._id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {contact.firstName} {contact.lastName}
                            </Typography>
                            {contact.isPrimary && (
                              <Chip label="Primary" size="small" color="success" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Email fontSize="small" color="action" />
                            {contact.email}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {contact.phone ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Phone fontSize="small" color="action" />
                              {contact.phone}
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{contact.title || '-'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business fontSize="small" color="action" />
                            {getAccountName(contact.account)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={contact.type || 'Other'}
                            size="small"
                            color={getTypeColor(contact.type)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewContact(contact)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Contact">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditContact(contact)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredContacts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* Contact Detail Dialog */}
        <Dialog open={openDetailDialog} onClose={handleCloseDetailDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Contact Details
          </DialogTitle>
          <DialogContent>
            {viewingContact && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight="bold">
                    {viewingContact.firstName} {viewingContact.lastName}
                  </Typography>
                  {viewingContact.isPrimary && (
                    <Chip label="Primary Contact" size="small" color="success" sx={{ mt: 1 }} />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography>{viewingContact.email}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography>{viewingContact.phone || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Mobile</Typography>
                  <Typography>{viewingContact.mobile || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Title</Typography>
                  <Typography>{viewingContact.title || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography>{viewingContact.department || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Box>
                    <Chip
                      label={viewingContact.type || 'Other'}
                      size="small"
                      color={getTypeColor(viewingContact.type)}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Account</Typography>
                  <Typography>{getAccountName(viewingContact.account)}</Typography>
                </Grid>
                {viewingContact.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography>{viewingContact.description}</Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetailDialog}>Close</Button>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => {
                handleCloseDetailDialog();
                if (viewingContact) handleEditContact(viewingContact);
              }}
              sx={{
                background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
              }}
            >
              Edit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Contact Dialog */}
        <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Edit Contact
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={editFormData.firstName}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={editFormData.lastName}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={editFormData.email}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={editFormData.phone}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile"
                  name="mobile"
                  value={editFormData.mobile}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={editFormData.title}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Department"
                  name="department"
                  value={editFormData.department}
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Type"
                  name="type"
                  value={editFormData.type}
                  onChange={handleFormChange}
                >
                  <MenuItem value="Decision Maker">Decision Maker</MenuItem>
                  <MenuItem value="Influencer">Influencer</MenuItem>
                  <MenuItem value="End User">End User</MenuItem>
                  <MenuItem value="Technical">Technical</MenuItem>
                  <MenuItem value="Executive">Executive</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="description"
                  value={editFormData.description}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSaveContact}
              disabled={saving || !editFormData.firstName || !editFormData.lastName || !editFormData.email}
              sx={{
                background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
};

export default ContactsPage;
